import {
  Deferred,
  MethodInfo,
  NextUnaryFn,
  RpcError,
  RpcInterceptor,
  RpcMetadata,
  RpcOptions,
  RpcStatus,
  UnaryCall,
} from '@protobuf-ts/runtime-rpc'

import { refreshClientSession } from 'shared/utils/auth/v2/client-session'
import { isUserError, UserError } from 'shared/utils/errors'
import { makeErrorFromRpcError } from 'shared/utils/errors/rpc.utils'

import logger from '../logger'

export class ClientAuthRetryInterceptor implements RpcInterceptor {
  private retryingMap = new WeakMap<UnaryCall, boolean>()

  private hasRetried(call: UnaryCall): boolean {
    return this.retryingMap.get(call) ?? false
  }

  private async retry(
    deferred: DeferredSet,
    next: NextUnaryFn,
    method: MethodInfo,
    input: object,
    options: RpcOptions,
  ): Promise<void> {
    try {
      const session = await refreshClientSession()

      if (!session?.accessToken) {
        throw new UserError('Failed to refresh session: no session returned')
      }

      if (!options.meta) options.meta = {}
      options.meta.Authorization = `Bearer ${session.accessToken}`

      const call = next(method, input, options)
      const result = await call

      resolveAll(deferred, result)
    } catch (error) {
      rejectAll(deferred, makeErrorFromRpcError(error))
    }
  }

  interceptUnary(
    next: NextUnaryFn,
    method: MethodInfo,
    input: object,
    options: RpcOptions,
  ): UnaryCall {
    const defHeader = new Deferred<RpcMetadata>()
    const defMessage = new Deferred<object>()
    const defStatus = new Deferred<RpcStatus>()
    const defTrailer = new Deferred<RpcMetadata>()
    const deferred = { defHeader, defMessage, defStatus, defTrailer }

    const call = next(method, input, options)

    void (async () => {
      try {
        const result = await call

        resolveAll(deferred, result)
      } catch (error) {
        if (this.hasRetried(call)) {
          rejectAll(deferred, error)
          return
        }

        this.retryingMap.set(call, true)

        if (isUnauthenticatedError(error)) {
          logger.debug('client session is not authenticated, retrying')
          await this.retry(deferred, next, method, input, options)
          return
        }

        rejectAll(deferred, error)
      }
    })()

    return new UnaryCall(
      method,
      options.meta ?? {},
      input,
      defHeader.promise,
      defMessage.promise,
      defStatus.promise,
      defTrailer.promise,
    )
  }
}

function isUnauthenticatedError(error: unknown): boolean {
  return (
    (isUserError(error) &&
      error.metadata?.grpcStatusCode === 'UNAUTHENTICATED') ||
    (error instanceof RpcError && error.code === 'UNAUTHENTICATED')
  )
}

interface DeferredSet {
  defHeader: Deferred<RpcMetadata>
  defMessage: Deferred<object>
  defStatus: Deferred<RpcStatus>
  defTrailer: Deferred<RpcMetadata>
}

function rejectAll(deferreds: DeferredSet, error: unknown): void {
  deferreds.defHeader.rejectPending(error)
  deferreds.defMessage.rejectPending(error)
  deferreds.defStatus.rejectPending(error)
  deferreds.defTrailer.rejectPending(error)
}

function resolveAll(
  deferreds: DeferredSet,
  result: {
    headers: RpcMetadata
    response: object
    status: RpcStatus
    trailers: RpcMetadata
  },
): void {
  deferreds.defHeader.resolve(result.headers)
  deferreds.defMessage.resolve(result.response)
  deferreds.defStatus.resolve(result.status)
  deferreds.defTrailer.resolve(result.trailers)
}
